import React from 'react';
import { GripVertical, Eye, EyeOff, Star, ArrowUp, ArrowDown, Plus } from 'lucide-react';
import { ConfiguredTab, getTabSourceLabel } from '../../../services/queries/tabConfigQueries';

interface TabSelectorProps {
  availableTabs: ConfiguredTab[];
  activeTabs: ConfiguredTab[];
  onAddTab: (tab: ConfiguredTab) => void;
  onRemoveTab: (tabId: string) => void;
  onToggleVisibility: (tabId: string) => void;
  onSetDefault: (tabId: string) => void;
  onReorder: (tabId: string, direction: 'up' | 'down') => void;
}

export const TabSelector: React.FC<TabSelectorProps> = ({
  availableTabs,
  activeTabs,
  onAddTab,
  onRemoveTab,
  onToggleVisibility,
  onSetDefault,
  onReorder
}) => {
  return (
    <div className="grid grid-cols-2 gap-4">
      {/* Available Tabs */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
          Available Tabs ({availableTabs.length})
        </h3>
        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-3 min-h-[300px]">
          {availableTabs.length > 0 ? (
            <div className="space-y-2">
              {availableTabs.map((tab) => (
                <div
                  key={tab.id}
                  className="p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-600 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {tab.label}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {getTabSourceLabel(tab.source)}
                        {tab.relatedObject && ` - ${tab.relatedObject}`}
                      </div>
                    </div>
                    <button
                      onClick={() => onAddTab(tab)}
                      className="ml-2 p-1.5 text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded transition-colors"
                      title="Add to active tabs"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-sm text-gray-500 dark:text-gray-400">
              All tabs are active
            </div>
          )}
        </div>
      </div>

      {/* Active Tabs */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
          Active Tabs ({activeTabs.length})
        </h3>
        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-3 min-h-[300px]">
          {activeTabs.length > 0 ? (
            <div className="space-y-2">
              {activeTabs.map((tab, index) => (
                <div
                  key={tab.id}
                  className={`p-3 rounded border transition-colors ${
                    tab.isVisible
                      ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                      : 'bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600 opacity-60'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <GripVertical className="w-4 h-4 text-gray-400 flex-shrink-0 mt-1 cursor-move" />

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {tab.label}
                        </span>
                        {tab.isDefault && (
                          <Star className="w-3.5 h-3.5 text-yellow-600 dark:text-yellow-400 fill-current flex-shrink-0" />
                        )}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {getTabSourceLabel(tab.source)}
                        {tab.relatedObject && ` - ${tab.relatedObject}`}
                      </div>
                    </div>

                    <div className="flex items-center gap-1 flex-shrink-0">
                      {/* Visibility Toggle */}
                      <button
                        onClick={() => onToggleVisibility(tab.id)}
                        className={`p-1.5 rounded transition-colors ${
                          tab.isVisible
                            ? 'text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20'
                            : 'text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                        title={tab.isVisible ? 'Hide tab' : 'Show tab'}
                      >
                        {tab.isVisible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>

                      {/* Set Default */}
                      <button
                        onClick={() => onSetDefault(tab.id)}
                        className={`p-1.5 rounded transition-colors ${
                          tab.isDefault
                            ? 'text-yellow-600 dark:text-yellow-400'
                            : 'text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                        title="Set as default tab"
                      >
                        <Star className={`w-4 h-4 ${tab.isDefault ? 'fill-current' : ''}`} />
                      </button>

                      {/* Move Up */}
                      {index > 0 && (
                        <button
                          onClick={() => onReorder(tab.id, 'up')}
                          className="p-1.5 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                          title="Move up"
                        >
                          <ArrowUp className="w-4 h-4" />
                        </button>
                      )}

                      {/* Move Down */}
                      {index < activeTabs.length - 1 && (
                        <button
                          onClick={() => onReorder(tab.id, 'down')}
                          className="p-1.5 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                          title="Move down"
                        >
                          <ArrowDown className="w-4 h-4" />
                        </button>
                      )}

                      {/* Remove */}
                      <button
                        onClick={() => onRemoveTab(tab.id)}
                        className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                        title="Remove tab"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-sm text-gray-500 dark:text-gray-400">
              No active tabs. Add tabs from the left panel.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
